export type LeadSource = 'website' | 'referral' | 'cold_call' | 'trade_show' | 'social_media' | 'advertisement' | 'other';
export type LeadStatus = 'new' | 'qualified' | 'disqualified' | 'converted';
export type LeadPriority = 'low' | 'medium' | 'high' | 'urgent';
export type DealStatus = 'open' | 'won' | 'lost';
export type ActivityType = 'call' | 'meeting' | 'email' | 'task' | 'note';
export type CompanySize = '1-10' | '11-50' | '51-200' | '201-500' | '500+';
export type ReminderStatus = 'pending' | 'done';
export type AttachmentEntityType = 'lead' | 'deal' | 'reminder' | 'activity' | 'note';
export type CustomerType = 'air_sanitizer_distribution' | 'syringes_distribution' | 'blood_collection_tubes_distribution' | 'machine_manufacturing';

// Constants for form selects
export const LEAD_SOURCES: LeadSource[] = ['website', 'referral', 'cold_call', 'trade_show', 'social_media', 'advertisement', 'other'];
export const LEAD_STATUSES: LeadStatus[] = ['new', 'qualified', 'disqualified'];
export const LEAD_PRIORITIES: LeadPriority[] = ['low', 'medium', 'high', 'urgent'];
export const ACTIVITY_TYPES: ActivityType[] = ['call', 'meeting', 'email', 'task', 'note'];
export const CUSTOMER_TYPES: CustomerType[] = ['air_sanitizer_distribution', 'syringes_distribution', 'blood_collection_tubes_distribution', 'machine_manufacturing'];
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  air_sanitizer_distribution: 'Air Sanitizer Distribution',
  syringes_distribution: 'Syringes Distribution',
  blood_collection_tubes_distribution: 'Blood Collection Tubes Distribution',
  machine_manufacturing: 'Machine Manufacturing',
};
export const PRODUCT_SKUS = [
  'Non-sterile syringes components – 2ml',
  'Non-sterile syringes components – 3ml',
  'Non-sterile syringes components – 5ml',
  'Non-sterile syringes components – 10ml',
  'Non-sterile syringes components – 20ml',
  'Non-sterile syringes components – 50ml',
  'Sterile syringes – 2ml',
  'Sterile syringes – 3ml',
  'Sterile syringes – 5ml',
  'Sterile syringes – 10ml',
  'Sterile syringes – 20ml',
  'Sterile syringes – 50ml',
  'Chocolate syringes',
  'Needles',
  'AD syringes',
  'Gaskets',
  'Blood collection tubes',
  'Blood collection tubes machines',
  'Air-Sanitizer – Marvel',
  'Air-Sanitizer – Marvel SS',
  'Air-Sanitizer – Marvel SS Pro',
  'Air-Sanitizer – Marvel Pro',
  'Air-Sanitizer – Breeze',
  'Air-Sanitizer – Travel-Buddy',
  'Air-Sanitizer – Bioshield',
  'Air-Sanitizer – Swatch Air',
  'Air-Sanitizer – Aura',
  'Electronic stethoscope',
  'IV sets machines',
  'IV cannula machines',
  'Special purpose machine',
  'Blister packing machine',
  'Ribbon packing machine',
  'ETO sterilizer machine',
  'Syringe making machines',
] as const;
export type ProductSKU = typeof PRODUCT_SKUS[number];
export const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education',
  'Real Estate', 'Construction', 'Transportation', 'Hospitality', 'Media',
  'Telecommunications', 'Energy', 'Agriculture', 'Legal', 'Consulting', 'Other'
] as const;

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

export interface Company {
  id: string;
  name: string;
  industry?: string;
  size?: CompanySize;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  description?: string;
  notes?: string;
  erp_customer_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface Contact {
  id: string;
  company_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  job_title?: string;
  department?: string;
  is_primary: boolean;
  linkedin_url?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  company_name?: string;
}

export interface Lead {
  id: string;
  lead_number: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  job_title?: string;
  email?: string;
  phone?: string;
  source: LeadSource;
  status: LeadStatus;
  priority: LeadPriority;
  estimated_value: number;
  customer_type?: CustomerType;
  product_skus?: string[];
  notes?: string;
  assigned_to?: string;
  converted_to_company_id?: string;
  converted_to_deal_id?: string;
  converted_at?: string;
  converted_by?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  assigned_to_name?: string;
  created_by_name?: string;
  converted_company_name?: string;
}

export interface DealStage {
  id: string;
  name: string;
  order_index: number;
  probability: number;
  color: string;
  is_won: boolean;
  is_lost: boolean;
  is_active: boolean;
  created_at: string;
}

export interface Deal {
  id: string;
  deal_number: string;
  title: string;
  company_id?: string;
  contact_id?: string;
  lead_id?: string;
  stage_id: string;
  stage?: string;
  value: number;
  currency: string;
  probability: number;
  expected_close_date?: string;
  actual_close_date?: string;
  status: DealStatus;
  lost_reason?: string;
  description?: string;
  source?: string;
  customer_type?: CustomerType;
  product_skus?: string[];
  assigned_to?: string;
  erp_project_id?: string;
  erp_quotation_id?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  company_name?: string;
  contact_name?: string;
  contact_email?: string;
  assigned_to_name?: string;
  created_by_name?: string;
  stage_probability?: number;
  stage_color?: string;
  is_won?: boolean;
  is_lost?: boolean;
  // Lead reference
  lead_number?: string;
}

export interface Activity {
  id: string;
  type: ActivityType;
  subject: string;
  description?: string;
  company_id?: string;
  contact_id?: string;
  lead_id?: string;
  deal_id?: string;
  due_date?: string;
  completed_at?: string;
  is_completed: boolean;
  outcome?: string;
  duration_minutes?: number;
  assigned_to?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  company_name?: string;
  contact_name?: string;
  lead_number?: string;
  lead_company_name?: string;
  deal_number?: string;
  deal_title?: string;
  assigned_to_name?: string;
  created_by_name?: string;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
  entity_type: 'lead' | 'contact' | 'company' | 'deal';
  created_at: string;
}

export interface EntityTag {
  id: string;
  tag_id: string;
  entity_id: string;
  entity_type: 'lead' | 'contact' | 'company' | 'deal';
  created_at: string;
}

// Dashboard Stats
export interface DashboardStats {
  total_leads: number;
  new_leads: number;
  qualified_leads: number;
  converted_leads: number;
  conversion_rate: number;
  open_deals: number;
  pipeline_value: number;
  deals_won: number;
  deals_won_value: number;
  deals_lost: number;
  activities_due_today: number;
  activities_overdue: number;
}

export interface PipelineSummary {
  stage: string;
  stage_order: number;
  stage_color: string;
  deal_count: number;
  total_value: number;
}

// Form Types
export interface LeadFormData {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  company_name?: string;
  job_title?: string;
  source: LeadSource;
  status: LeadStatus;
  priority?: LeadPriority;
  estimated_value?: number;
  customer_type?: CustomerType;
  product_skus?: string[];
  notes?: string;
  assigned_to?: string;
}

export interface CompanyFormData {
  name: string;
  industry?: string;
  size?: CompanySize;
  website?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  description?: string;
  notes?: string;
}

export interface ContactFormData {
  company_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  job_title?: string;
  department?: string;
  is_primary?: boolean;
  linkedin_url?: string;
  notes?: string;
}

export interface DealFormData {
  title: string;
  company_id?: string;
  contact_id?: string;
  company_name?: string;
  contact_name?: string;
  stage_id: string;
  value: number;
  currency: string;
  probability?: number;
  expected_close_date?: string;
  description?: string;
  source?: string;
  customer_type?: CustomerType;
  product_skus?: string[];
  notes?: string;
  assigned_to?: string;
}

export interface ActivityFormData {
  type: ActivityType;
  subject: string;
  description?: string;
  company_id?: string;
  contact_id?: string;
  lead_id?: string;
  deal_id?: string;
  due_date?: string;
  assigned_to?: string;
}

// Filter Types
export interface LeadFilters {
  status?: LeadStatus;
  source?: LeadSource;
  priority?: LeadPriority;
  assigned_to?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface DealFilters {
  stage?: string;
  status?: DealStatus;
  assigned_to?: string;
  company_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface ActivityFilters {
  type?: ActivityType;
  is_completed?: boolean;
  assigned_to?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// Reminder Types
export interface Reminder {
  id: string;
  lead_id?: string;
  deal_id?: string;
  title: string;
  due_date: string;
  priority: LeadPriority;
  notes?: string;
  status: ReminderStatus;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  created_by_name?: string;
  attachments?: Attachment[];
}

export interface ReminderFormData {
  lead_id?: string;
  deal_id?: string;
  title: string;
  due_date: string;
  priority: LeadPriority;
  notes?: string;
  status?: ReminderStatus;
}

// Note Types
export interface Note {
  id: string;
  lead_id?: string;
  deal_id?: string;
  content: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
}

export interface NoteFormData {
  lead_id?: string;
  deal_id?: string;
  content: string;
}

// Attachment Types
export interface Attachment {
  id: string;
  entity_type: AttachmentEntityType;
  entity_id: string;
  file_name: string;
  file_type?: string;
  file_size?: number;
  file_url: string;
  storage_path: string;
  uploaded_by?: string;
  created_at: string;
  // Joined fields
  uploaded_by_name?: string;
}
